import { useT } from "../i18n";
import { Erklaerung } from "./index";
import { Zeitraum, type Spanne } from "./Zeitraum";
import { alsTag } from "../daten/kalender";
import type { Schritt } from "../daten/preisbuendel";

/* ══════════════════════════════════════════════════════════════════
   Der Zeitraum in drei Formen — ein Feld.

   docs/38-vermietmodul.md: *„Der Zeitraum wird gefragt, wie der Betrieb
   ihn nennt — drei Formen, ein Feld: **Kalenderwoche** („KW 39"),
   **Zeitraum** („21.09.–25.09.") oder **Dauer ab Tag** („ab Montag, 5
   Miettage"). Die Miettage stehen immer daneben, samt Grund, wenn der
   Kalender Tage streicht."*

   **Warum drei und nicht eine.** Am Telefon fällt der Zeitraum in genau
   diesen drei Sätzen: „Nächste Woche" (die Kalenderwoche ist im Bau die
   Einheit, in der geplant wird), „vom 21. bis zum 25." und „ab Montag
   für fünf Tage". Wer daraus ein Datumsfeld macht, lässt den Disponenten
   rechnen — und zwar bei jedem Anruf, denn die drei Sätze sind nicht
   dasselbe: Fünf Miettage ab Donnerstag enden bei Fünftagekalender am
   Mittwoch darauf und nicht am Montag.

   **Umschalten verliert nichts.** Der Anfangstag trägt durch alle drei
   Formen; das Ende trägt, wo die Form es ausdrücken kann. Auf die
   Kalenderwoche umzuschalten setzt Montag bis Freitag der Woche, in der
   der Anfang liegt — das ist die Bedeutung von „KW 39" und keine
   Vorgabe, die sich jemand ausgedacht hat. Was dabei herauskommt, steht
   darunter, in allen drei Formen dieselbe Zeile: Anfang, Ende,
   Miettage. Ein Umschalter, dessen Wirkung man nicht sieht, ist ein
   zweites Formular mit einer zweiten Antwort.

   **Die Miettage rechnet diese Maske nicht.** Welcher Kalender gilt,
   hängt an Typ, Gruppe, Übergruppe und Betrieb — das weiß nur die andere
   Seite (`GET /preise`, `fach.miettageErklaert`). Hier steht die Zahl,
   die von dort kommt, und der Grund daneben, wenn der Kalender Tage
   streicht. Eine eigene Rechnung wäre eine zweite Wahrheit.

   Klassen: `zf-*`, gegen alle Stile geprüft.
   ══════════════════════════════════════════════════════════════════ */

export type Zeitart = "kw" | "spanne" | "dauer";

/**
 * Die gestellte Zeitfrage.
 *
 * `von` steht immer. `bis` steht in den beiden Formen, die ein Ende nennen —
 * bei „Dauer ab Tag" rechnet es die API, und bis ihre Antwort da ist, steht
 * hier `null`. Eine gerechnete Zahl hier abzulegen hieße, sie zweimal zu
 * führen.
 */
export type Zeitwunsch = {
  art: Zeitart;
  von: string;
  bis: string | null;
  /** Die gefragten **Miettage** bei `dauer`. In den anderen Formen unbenutzt. */
  tage: number;
};

/** Die Vorbelegung: ab morgen, fünf Miettage — eine Arbeitswoche (Regel 11). */
export const VORGABE_MIETTAGE = 5;

export function zeitVorgabe(): Zeitwunsch {
  const morgen = new Date();
  morgen.setDate(morgen.getDate() + 1);
  const von = alsTag(morgen);
  return { art: "spanne", von, bis: null, tage: VORGABE_MIETTAGE };
}

/* ══════════════ Die Kalenderwoche ══════════════ */

function ausIso(iso: string): Date {
  const [j, m, t] = iso.split("-").map(Number);
  return new Date(j ?? 1970, (m ?? 1) - 1, t ?? 1);
}

/** Der Montag der Woche, in der dieser Tag liegt. */
export function montagVon(iso: string): string {
  const d = ausIso(iso);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return alsTag(d);
}

/**
 * Die Kalenderwoche nach ISO 8601 — die, die im Bau „KW 39" heißt.
 *
 * Nicht selbst gezählt: Der Donnerstag entscheidet, zu welchem Jahr eine
 * Woche gehört, und der 1. Januar liegt oft in der KW 52 des Vorjahrs. Wer
 * das mit einer Division löst, liegt in jedem zweiten Januar daneben.
 */
export function kalenderwoche(iso: string): { jahr: number; woche: number } {
  const d = ausIso(iso);
  // Auf den Donnerstag derselben Woche — er trägt das Jahr der Woche.
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const jahr = d.getFullYear();
  const ersterDonnerstag = new Date(jahr, 0, 4);
  ersterDonnerstag.setDate(
    ersterDonnerstag.getDate() + 3 - ((ersterDonnerstag.getDay() + 6) % 7),
  );
  const woche = 1 + Math.round((d.getTime() - ersterDonnerstag.getTime()) / (7 * 86_400_000));
  return { jahr, woche };
}

function tagePlus(iso: string, tage: number): string {
  const d = ausIso(iso);
  d.setDate(d.getDate() + tage);
  return alsTag(d);
}

/**
 * Was „KW 39" als Zeitraum bedeutet: **Montag bis Freitag**.
 *
 * Nicht bis Sonntag. Wer eine Maschine für eine Woche mietet, holt sie
 * montags und bringt sie freitags; das Wochenende liegt bei
 * Fünftagekalender ohnehin nicht in der Rechnung, und ein Balken, der bis
 * Sonntag steht, sperrt zwei Tage, die niemand gemeint hat. Das Beispiel im
 * Modulentwurf sagt es genauso: „KW 39 · 21.09.–25.09. · 5".
 */
export function wocheAls(iso: string): { von: string; bis: string } {
  const montag = montagVon(iso);
  return { von: montag, bis: tagePlus(montag, 4) };
}

/* ══════════════ Umschalten ══════════════ */

/**
 * Die Form wechseln, ohne die Frage zu verlieren.
 *
 * `endeJetzt` ist das Ende, das gerade gilt — in der Dauerform hat es die
 * API gerechnet, und nur sie kennt es. Ohne diese Angabe wüsste der
 * Wechsel zum Zeitraum kein Ende und müsste eins erfinden.
 */
export function zeitartWechseln(
  wert: Zeitwunsch,
  neu: Zeitart,
  endeJetzt: string | null,
  miettageJetzt: number | null,
): Zeitwunsch {
  if (neu === wert.art) return wert;
  if (neu === "kw") {
    const woche = wocheAls(wert.von);
    return { ...wert, art: "kw", von: woche.von, bis: woche.bis };
  }
  if (neu === "spanne") {
    return { ...wert, art: "spanne", bis: wert.bis ?? endeJetzt ?? wert.von };
  }
  // Auf die Dauer: Die gezählten Miettage von eben sind die gefragten von
  // jetzt. Sie sind nicht die Kalendertage — genau darin liegt der Wechsel.
  return {
    ...wert,
    art: "dauer",
    bis: null,
    tage: Math.max(1, Math.min(365, miettageJetzt ?? wert.tage)),
  };
}

/* ══════════════ Das Feld ══════════════ */

const ARTEN: { art: Zeitart; wort: string }[] = [
  /* **Der Zeitraum steht vorn, und er ist die Vorgabe** (Luca, 17.09.2026:
     *„im Standard soll Zeitraum ausgewählt sein"*). Er stand schon vorgewählt
     da — aber als zweiter von drei Knöpfen, und der erste liest sich wie die
     Vorgabe. Was die Vorgabe ist, steht jetzt auch links. */
  { art: "spanne", wort: "zeitfrage.artSpanne" },
  { art: "kw", wort: "zeitfrage.artKw" },
  { art: "dauer", wort: "zeitfrage.artDauer" },
];

export function Zeitfrage({
  id, wert, aendern, miettage, schritte, ende, gemischt = false, arten, ohneErgebnis = false,
}: {
  id: string;
  wert: Zeitwunsch;
  aendern: (neu: Zeitwunsch) => void;
  /** Die gezählten Miettage, wie die API sie meldet. `null`: noch keine Antwort. */
  miettage: number | null;
  /** Warum aus acht Tagen sechs werden — Schlüssel mit Zahlen, kein fertiger Satz. */
  schritte: Schritt[];
  /** Das gerechnete Ende, wenn nach einer Dauer gefragt wurde. */
  ende: string | null;
  /** Die Zeilen zählen nach verschiedenen Mietkalendern — dann keine Zahl, aber auch kein „lädt" (N10-01). */
  gemischt?: boolean;
  /**
   * Welche Formen der Umschalter anbietet — ohne Angabe alle drei. Vermieten
   * fragt seit dem 07.10.2026 nur „Zeitraum | KW" (Luca: die Dauer entfällt).
   */
  arten?: Zeitart[];
  /**
   * Ohne die Zeile unter dem Feld (Spanne, Miettage, KW). Vermieten nennt
   * den Zeitraum genau einmal — im Feld; die Miettage stehen an jeder
   * Trefferzeile (07.10.2026).
   */
  ohneErgebnis?: boolean;
}) {
  const t = useT();
  const woche = kalenderwoche(wert.von);
  const bisJetzt = wert.bis ?? ende;

  /* Der Grund steht nur da, wo es einen gibt. „5 Miettage" ist die Antwort;
     „drei Tage fallen weg, weil der Betrieb fünf Tage zählt" ist der Grund,
     und den braucht nur, wer sich über die Zahl wundert. `preis.miettage`
     ist kein Grund — das ist das Ergebnis, und es steht schon daneben. */
  const gruende = schritte.filter((s) => s.key !== "preis.miettage");

  const setze = (teil: Partial<Zeitwunsch>) => aendern({ ...wert, ...teil });

  return (
    <div className="zf">
      <div className="zf-kopf">
        <label className="zf-schild" htmlFor={id}>{t("zeitfrage.schild")}</label>
        {/* Drei Wörter, kein Auswahlfeld: Die Form ist die Frage und nicht
            eine Einstellung dazu. Wer sie sieht, sieht auch, welche gilt. */}
        <div className="zf-arten" role="group" aria-label={t("zeitfrage.formWaehlen")}>
          {ARTEN.filter((a) => !arten || arten.includes(a.art)).map((a) => (
            <button
              key={a.art}
              type="button"
              className="zf-art"
              aria-pressed={wert.art === a.art}
              onClick={() => aendern(zeitartWechseln(wert, a.art, ende, miettage))}
            >
              {t(a.wort)}
            </button>
          ))}
        </div>
      </div>

      {/* ── Ein Feld, und zwar das der Anwendung ──────────────────────
          Luca, 17.09.2026: *„Der Date Picker ist ganz merkwürdig
          gemacht."* Er war es: Die Kalenderwoche hatte ein Zahlenfeld mit
          Pfeilen, die Dauer ein `type="date"` des Browsers — drei Formen,
          drei Bedienungen, und keine davon die, die im Brett, im Angebot
          und in der Schnellerfassung steht.

          **Jetzt steht in allen drei Formen derselbe Zeitraumwähler**
          (`bausteine/Zeitraum.tsx`): tippen, eine Dauer wählen oder im
          Kalender klicken. KW und Dauer sind keine zweiten Felder mehr,
          sondern **Abkürzungen daneben**, die in dasselbe Feld schreiben —
          und genau das sind sie auch: „KW 39" ist ein Zeitraum, den man
          nicht ausrechnen will, und „fünf Miettage" eine Länge, die der
          Kalender in ein Ende übersetzt. */}
      <div className="zf-zeile">
        {wert.art === "kw" ? (
          <span className="zf-kw" role="group" aria-label={t("zeitfrage.kwSchild")}>
            <button
              type="button"
              className="zf-pfeil"
              aria-label={t("zeitfrage.wocheZurueck")}
              onClick={() => setze(wocheAls(tagePlus(wert.von, -7)))}
            >
              ‹
            </button>
            <span className="zf-kw-wort">{t("zeitfrage.kwNeben", { woche: woche.woche })}</span>
            <button
              type="button"
              className="zf-pfeil"
              aria-label={t("zeitfrage.wocheVor")}
              onClick={() => setze(wocheAls(tagePlus(wert.von, 7)))}
            >
              ›
            </button>
          </span>
        ) : null}

        <span className="zf-feld">
          <Zeitraum
            id={id}
            /* **Das Ende, das gilt, steht im Feld** — auch wenn es der Kalender
               gerechnet hat (Vorbelegung, Dauer). Bis zur Prüfrunde Runde 11
               stand hier nur `wert.bis`: Bei der Vorbelegung zeigte das Feld
               „06.10.2026" statt „06.10. – 12.10.2026", und der Kalender hielt
               den ersten Klick für das Ende. */
            wert={{ von: wert.von, bis: wert.bis ?? ende } as Spanne}
            ohneTage={ohneErgebnis}
            aendern={(neu) => setze({
              von: neu.von ?? wert.von,
              /* **In der Dauerform bleibt das Ende offen.** Wer dort ein
                 Ende im Kalender anklickt, hat aufgehört, nach einer Dauer
                 zu fragen — also gilt wieder der Zeitraum. Sonst stünde ein
                 Ende im Feld, das die nächste Antwort überschreibt. */
              ...(wert.art === "dauer" && neu.bis ? { art: "spanne" as const } : {}),
              bis: neu.bis ?? null,
            })}
          />
        </span>

        {wert.art === "dauer" ? (
          <span className="zf-dauer">
            <input
              className="feld feld-zahl zf-tage"
              type="number"
              min={1}
              max={365}
              step={1}
              aria-label={t("zeitfrage.miettageFeld")}
              value={wert.tage}
              onChange={(e) => {
                const zahl = Number(e.target.value);
                if (!Number.isFinite(zahl) || zahl < 1 || zahl > 365) return;
                setze({ tage: Math.round(zahl), bis: null });
              }}
            />
            <span className="zf-dauer-wort">{t("zeitfrage.miettageWort")}</span>
          </span>
        ) : null}
      </div>

      {/* **Die Antwort auf die gestellte Frage, in allen drei Formen dieselbe
          Zeile.** Sie ist der Beweis, dass der Umschalter nichts verloren
          hat — und in der Dauerform die einzige Stelle, an der überhaupt
          steht, bis wann. */}
      {ohneErgebnis ? null : (
      <p className="zf-ergebnis">
        <span className="zf-spanne">
          {bisJetzt
            ? t("zeitfrage.vonBis", { von: kurz(wert.von), bis: kurz(bisJetzt) })
            : t("zeitfrage.abTagOhneEnde", { von: kurz(wert.von) })}
        </span>
        <span className="zf-miettage">
          {miettage === null
            ? t(gemischt ? "zeitfrage.miettageJeGeraet" : "allgemein.laedt")
            : miettage === 1
              ? t("preisauskunft.einMiettag")
              : t("preisauskunft.miettage", { zahl: miettage })}
          {/* **Wessen Zahl hier steht** (17.09.2026). Sie kommt aus dem
              Mietkalender des Betriebs, denn gefragt ist noch kein Gerät
              (`GET /preise` ohne `typ`, siehe `routen/preis.ts`). Die
              Trefferzeile darunter trägt die Zahl IHRES Typs — und wo der
              einen anderen Kalender hat, stehen zwei verschiedene Zahlen
              übereinander. Das ist richtig gerechnet und liest sich wie ein
              Fehler; deshalb steht die Erklärung jetzt immer da und sagt in
              einem Satz, was den Unterschied macht. */}
          {gruende.length ? (
            <Erklaerung
              k="zeitfrage.warumWas"
              werte={{ grund: gruende.map((s) => t(s.key, s as never)).join(" · ") }}
            />
          ) : (
            <Erklaerung k="zeitfrage.vorgabeWas" />
          )}
        </span>
        {/* Die Kalenderwoche steht auch dann daneben, wenn nicht nach ihr
            gefragt wurde — im Bau wird in Wochen geplant, und „das ist die
            39." beantwortet die nächste Frage, bevor sie kommt. In der
            KW-Form steht sie schon links am Feld. */}
        {wert.art === "kw" ? null : (
          <span className="zf-kw-neben">
            {t("zeitfrage.kwNeben", { woche: woche.woche })}
          </span>
        )}
      </p>
      )}
    </div>
  );
}

/** `2026-09-21` → `21.09.` — das Jahr steht am Telefon nie zur Debatte. */
function kurz(iso: string): string {
  const d = ausIso(iso);
  return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.`;
}
